export type LayoutType = 'sideBySide' | 'pip' | 'medallion' | 'focus';

export type CornerPosition =
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight';

export type DeviceConfig = {
  deviceId: string;
  active: boolean;
};

// Base options shared by all layouts
type BaseLayoutOptions = {
  devices?: DeviceConfig[]; // first = primary, rest = secondary (in order), active determines inclusion
  maxDevices?: number; // max devices to show
};

export type SideBySideOptions = BaseLayoutOptions & {
  layout: 'sideBySide';
  // maxDevices default: 9
  gap?: number; // gap between cells in px, default: 4
  gapColor?: string; // hex/rgb color for gap/background, default: #000000
  columns?: number; // number of columns, default: 2
  rows?: number; // number of rows, default: 1
};

export type PipOptions = BaseLayoutOptions & {
  layout: 'pip';
  // maxDevices default: 3 (1 primary + 2 secondary)
  position?: CornerPosition; // PIP corner, default: bottomRight
  pipStack?: 'vertical' | 'horizontal'; // stack direction, default: vertical
  pipSpacing?: number; // spacing in px, default: 10
  secondarySize?: number; // percentage 0-100, default: 25
  pipBorderColor?: string; // hexa/rgba color for PIP border, default: rgba(255,255,255,0.8)
  pipBorderSize?: number; // border width in px, default: 0
};

export type MedallionOptions = BaseLayoutOptions & {
  layout: 'medallion';
  // maxDevices default: 4 (1 primary + 3 secondary)
  medallionSize?: number; // percentage 0-100, default: 20
  medallionPosition?: CornerPosition; // corner, default: topRight
  medallionStack?: 'vertical' | 'horizontal'; // stack direction, default: vertical
  medallionSpacing?: number; // spacing in px, default: 15
  medallionBorderColor?: string; // hexa/rgba color for border, default: rgba(255,255,255,0.8)
  medallionBorderSize?: number; // border width in px, default: 3
};

export type FocusOptions = BaseLayoutOptions & {
  layout: 'focus';
  // maxDevices default: 1 (only primary)
};

export type LayoutOptions =
  | SideBySideOptions
  | PipOptions
  | MedallionOptions
  | FocusOptions;

// SrcInfo type with ratio information
export type SrcInfo = {
  deviceId: string;
  srcLink: string;
  srcLinkType?: string;
  orientation: 'portrait' | 'landscape';
  width: number;
  height: number;
  ratio: number; // width/height
  isDummy?: boolean;
};

export type SrcInfoByDeviceId = Record<string, SrcInfo>;

export type MultiStageOptionsPayload = {
  layoutOptions: LayoutOptions;
  srcInfoByDeviceId: SrcInfoByDeviceId;
};

// Default layout is PIP
export const getDefaultLayoutOptions = (): PipOptions => ({
  layout: 'pip',
  position: 'bottomRight',
  pipStack: 'vertical',
  pipSpacing: 10,
  secondarySize: 25,
});

// Default max devices per layout type
const DEFAULT_MAX_DEVICES: Record<LayoutType, number> = {
  focus: 1,
  pip: 3, // 1 primary + 2 PIPs
  medallion: 4, // 1 primary + 3 medallions
  sideBySide: 9, // 3x3 grid
};

// Prepare layout options: filter active devices, apply maxDevices limit, set defaults
export const prepareLayoutOptions = (
  layoutOptions: LayoutOptions,
): LayoutOptions => {
  const existingDevices = layoutOptions.devices || [];
  const maxDevices =
    layoutOptions.maxDevices ?? DEFAULT_MAX_DEVICES[layoutOptions.layout];

  // Filter active devices and truncate to maxDevices
  const devices = existingDevices.filter((d) => d.active).slice(0, maxDevices);

  return applyLayoutDefaults(layoutOptions, devices);
};

// Apply layout-specific defaults
const applyLayoutDefaults = (
  layoutOptions: LayoutOptions,
  devices: DeviceConfig[],
): LayoutOptions => {
  switch (layoutOptions.layout) {
    case 'focus':
      return { ...layoutOptions, devices };

    case 'sideBySide':
      return {
        ...layoutOptions,
        devices,
        gap: layoutOptions.gap ?? 4,
      };

    case 'pip':
      return {
        ...layoutOptions,
        devices,
        position: layoutOptions.position || 'bottomRight',
        pipStack: layoutOptions.pipStack || 'vertical',
        pipSpacing: layoutOptions.pipSpacing ?? 10,
        secondarySize: layoutOptions.secondarySize ?? 25,
      };

    case 'medallion':
      return {
        ...layoutOptions,
        devices,
        medallionPosition: layoutOptions.medallionPosition || 'topRight',
        medallionStack: layoutOptions.medallionStack || 'vertical',
        medallionSpacing: layoutOptions.medallionSpacing ?? 15,
        medallionSize: layoutOptions.medallionSize ?? 20,
      };
  }
};
